/**
 * Pagamenti fattura (embedded nella riga espansa).
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { CreditCard, XCircle, Plus } from 'lucide-react';
import { fmtEUR } from '../../lib/contractModificationApi';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => {
  const t = localStorage.getItem('token');
  return { headers: t ? { Authorization: `Bearer ${t}` } : {} };
};

const METHODS = [
  { v: 'bonifico', l: 'Bonifico' },
  { v: 'riba', l: 'RiBa' },
  { v: 'assegno', l: 'Assegno' },
  { v: 'contanti', l: 'Contanti' },
  { v: 'compensazione', l: 'Compensazione' },
  { v: 'altro', l: 'Altro' },
];

const SupplierInvoicePayments = ({ invoice, onChanged }) => {
  const [payments, setPayments] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    payment_date: new Date().toISOString().slice(0, 10),
    amount: '',
    method: 'bonifico',
    reference: '',
    notes: '',
  });

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/supplier-invoices/${invoice.id}/payments`, auth());
      setPayments(r.data?.payments || []);
    } catch (err) { /* silent: payments empty */ }
  }, [invoice.id]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!form.amount || Number(form.amount) <= 0) return toast.error('Importo obbligatorio');
    setBusy(true);
    try {
      await axios.post(`${API}/api/supplier-invoices/${invoice.id}/payments`, {
        ...form,
        amount: Number(form.amount),
      }, auth());
      toast.success('Pagamento registrato');
      setForm({ ...form, amount: '', reference: '', notes: '' });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore pagamento');
    } finally {
      setBusy(false);
    }
  };

  const voidPayment = async (pid) => {
    if (!window.confirm('Stornare questo pagamento?')) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/supplier-invoices/${invoice.id}/payments/${pid}/void`, {}, auth());
      toast.success('Pagamento stornato');
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore storno');
    } finally {
      setBusy(false);
    }
  };

  const active = payments.filter((p) => !p.voided);
  const totalPaid = active.reduce((s, p) => s + Number(p.amount || 0), 0);
  const residual = Number(invoice.net_to_pay || 0) - totalPaid;

  return (
    <div className="rounded-md bg-white border p-3" data-testid={`invoice-payments-${invoice.id.slice(0, 8)}`}>
      <div className="flex items-center gap-2 mb-2">
        <CreditCard className="w-4 h-4 text-slate-500" />
        <div className="text-sm font-medium">Pagamenti</div>
        <div className="text-xs text-slate-500 ml-auto">
          {fmtEUR(totalPaid)} pagati · residuo {fmtEUR(residual)}
        </div>
      </div>

      {/* Lista */}
      {payments.length > 0 && (
        <div className="mb-3 space-y-1">
          {payments.map((p) => (
            <div key={p.id} className={`grid grid-cols-12 gap-2 text-xs px-2 py-1 rounded ${p.voided ? 'bg-red-50 text-slate-400 line-through' : 'bg-slate-50'}`}>
              <div className="col-span-2">{p.payment_date}</div>
              <div className="col-span-2 text-right font-medium">{fmtEUR(p.amount)}</div>
              <div className="col-span-2">{p.method}</div>
              <div className="col-span-4 truncate">{p.reference || '—'}</div>
              <div className="col-span-2 text-right">
                {!p.voided && (
                  <Button size="sm" variant="ghost" onClick={() => voidPayment(p.id)}>
                    <XCircle className="w-3 h-3 text-red-500" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Nuovo pagamento */}
      {residual > 0.01 && (
        <div className="grid grid-cols-12 gap-2 items-end">
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500 uppercase">Data</label>
            <Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500 uppercase">Importo</label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid={`payment-amount-${invoice.id.slice(0, 8)}`} />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] text-slate-500 uppercase">Metodo</label>
            <select value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} className="border rounded px-2 py-1 text-xs w-full h-[34px]">
              {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
          </div>
          <div className="col-span-4">
            <label className="text-[10px] text-slate-500 uppercase">Riferimento (CRO, assegno…)</label>
            <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Button size="sm" onClick={add} disabled={busy} className="w-full" data-testid={`payment-add-${invoice.id.slice(0, 8)}`}>
              <Plus className="w-3 h-3 mr-1" /> Registra
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierInvoicePayments;
